// How We Do It -- position, size, layer and spacing of a block
// (block.settings.layout). Same rules as sanitizeBlockLayout in
// server_elakay/elakay_helpers/howWeDoItRules.js.
//
//   {
//     mode: "flow" | "free",  // flow: stays in the page flow, x/y shift it
//                             //   (position: relative) -- the others never move;
//                             // free: x/y place it anywhere in its container
//                             //   (position: absolute), over / under the others
//     x, y,                   // px
//     w, h,                   // px, 0 = automatic size
//     margin: {top,right,bottom,left}, padding: {...},   // px
//     z,                      // layer (0 = automatic), same on every device
//     tablet: {mode,x,y,w,h,margin,padding},   // optional own layout
//     mobile: {...},                            // optional own layout
//   }
//
// A device without its own layout uses the next larger one (mobile -> tablet
// -> desktop). Old layouts ({x, y, margin, padding}) are flow layouts.
// The positioning container of a block is its parent block (nested blocks)
// or the page's block list (top level): it follows the block tree.

export const LAYOUT_SIDES = ["top", "right", "bottom", "left"];
export const LAYOUT_DEVICES = ["desktop", "tablet", "mobile"];
export const LAYOUT_LIMITS = Object.freeze({
  offset: { min: -4000, max: 4000 },
  size: { min: 0, max: 4000 },
  margin: { min: -400, max: 400 },
  padding: { min: 0, max: 400 },
  z: { min: -1000, max: 1000 },
});

const toInt = (value, { min, max }) => {
  const number = Math.round(Number(value));
  if (!Number.isFinite(number)) return 0;
  return Math.min(max, Math.max(min, number));
};

const normalizeSides = (sides, limits) => {
  const source = sides && typeof sides === "object" ? sides : {};
  return LAYOUT_SIDES.reduce((out, side) => ({ ...out, [side]: toInt(source[side], limits) }), {});
};

const isObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);

/** The per-device part of a layout, complete and clamped. */
export const normalizeLayoutValues = (values) => {
  const source = isObject(values) ? values : {};
  return {
    mode: source.mode === "free" ? "free" : "flow",
    x: toInt(source.x, LAYOUT_LIMITS.offset),
    y: toInt(source.y, LAYOUT_LIMITS.offset),
    w: toInt(source.w, LAYOUT_LIMITS.size),
    h: toInt(source.h, LAYOUT_LIMITS.size),
    margin: normalizeSides(source.margin, LAYOUT_LIMITS.margin),
    padding: normalizeSides(source.padding, LAYOUT_LIMITS.padding),
  };
};

const sidesSet = (sides) => LAYOUT_SIDES.some((side) => sides[side] !== 0);

export const isDefaultValues = (values) => {
  const v = normalizeLayoutValues(values);
  return v.mode === "flow" && !v.x && !v.y && !v.w && !v.h && !sidesSet(v.margin) && !sidesSet(v.padding);
};

/** Complete, clamped layout (device layouts kept only when present). */
export const normalizeBlockLayout = (layout) => {
  const source = isObject(layout) ? layout : {};
  const out = { ...normalizeLayoutValues(source), z: toInt(source.z, LAYOUT_LIMITS.z) };
  if (isObject(source.tablet)) out.tablet = normalizeLayoutValues(source.tablet);
  if (isObject(source.mobile)) out.mobile = normalizeLayoutValues(source.mobile);
  return out;
};

/** True when the layout changes nothing at all. */
export const isDefaultLayout = (layout) => {
  const l = normalizeBlockLayout(layout);
  return isDefaultValues(l) && !l.z && !l.tablet && !l.mobile;
};

/** What is stored: the normalized layout, or undefined when it changes nothing. */
export const compactBlockLayout = (layout) => (isDefaultLayout(layout) ? undefined : normalizeBlockLayout(layout));

export const hasOwnDeviceLayout = (layout, device) =>
  device === "desktop" || (isObject(layout) && isObject(layout[device]));

/** The values that apply on a device (inheriting from the larger ones). */
export const getDeviceLayout = (layout, device = "desktop") => {
  const l = normalizeBlockLayout(layout);
  if (device === "mobile" && l.mobile) return l.mobile;
  if ((device === "mobile" || device === "tablet") && l.tablet) return l.tablet;
  return normalizeLayoutValues(l);
};

/**
 * A layout with new values for ONE device; the other devices are left as
 * they are. The first change on tablet/mobile starts from what that device
 * showed (its inherited values).
 */
export const setDeviceLayout = (layout, device, patch) => {
  const current = normalizeBlockLayout(layout);
  const next = normalizeLayoutValues({ ...getDeviceLayout(layout, device), ...patch });
  if (device === "desktop" || !LAYOUT_DEVICES.includes(device)) {
    return compactBlockLayout({ ...current, ...next });
  }
  return compactBlockLayout({ ...current, [device]: next });
};

/** Removes the own layout of tablet/mobile (back to inheriting), or the desktop values. */
export const clearDeviceLayout = (layout, device) => {
  const current = normalizeBlockLayout(layout);
  if (device === "tablet" || device === "mobile") {
    const { [device]: removed, ...rest } = current;
    return compactBlockLayout(rest);
  }
  const { tablet, mobile, z } = current;
  return compactBlockLayout({ z, ...(tablet ? { tablet } : {}), ...(mobile ? { mobile } : {}) });
};

export const setLayoutZ = (layout, z) => compactBlockLayout({ ...normalizeBlockLayout(layout), z: toInt(z, LAYOUT_LIMITS.z) });

/** Short description for the editor ("Libre · X 12 · Y -4 · 200×80"). */
export const describeLayoutValues = (values, labels = {}) => {
  if (!values || isDefaultValues(values)) return "";
  const v = normalizeLayoutValues(values);
  const parts = [];
  if (v.mode === "free") parts.push(labels.free || "free");
  if (v.x || v.y) parts.push(`X ${v.x} · Y ${v.y}`);
  if (v.w || v.h) parts.push(`${v.w || "auto"}×${v.h || "auto"}`);
  if (sidesSet(v.margin)) parts.push(labels.margin || "margin");
  if (sidesSet(v.padding)) parts.push(labels.padding || "padding");
  return parts.join(" · ");
};

// ---------------------------------------------------------------------------
// Layers: z order among the blocks of one container.
// ---------------------------------------------------------------------------

/**
 * New z values for the siblings of `blockId` after a layer action
 * ("front" | "back" | "forward" | "backward"): every sibling gets 1..n in
 * painting order, so the order is explicit and stable. Returns
 * { [blockId]: z } or null when nothing changes.
 */
export const computeLayerOrder = (siblings = [], blockId, action) => {
  const ranked = siblings
    .map((block, index) => ({ id: block.blockId, z: normalizeBlockLayout(block.settings?.layout).z, index }))
    .sort((a, b) => a.z - b.z || a.index - b.index)
    .map((item) => item.id);
  const from = ranked.indexOf(blockId);
  if (from === -1) return null;
  const to = { front: ranked.length - 1, back: 0, forward: Math.min(ranked.length - 1, from + 1), backward: Math.max(0, from - 1) }[action];
  if (to === undefined) return null;
  const next = [...ranked];
  next.splice(from, 1);
  next.splice(to, 0, blockId);
  const result = {};
  let changed = false;
  next.forEach((id, index) => {
    result[id] = index + 1;
    const before = siblings.find((block) => block.blockId === id);
    if (normalizeBlockLayout(before?.settings?.layout).z !== index + 1) changed = true;
  });
  return changed ? result : null;
};

// ---------------------------------------------------------------------------
// CSS of the page (injected in the preview / public frame, not inline): one
// rule set per block and per group of devices showing the same values.
// ---------------------------------------------------------------------------

const SAFE_ID = /^[A-Za-z0-9_-]{1,120}$/;
const DEVICE_PREFIX = {
  desktop: 'html:not([data-hwdi-device="tablet"]):not([data-hwdi-device="mobile"])',
  tablet: 'html[data-hwdi-device="tablet"]',
  mobile: 'html[data-hwdi-device="mobile"]',
};

const px = (value) => `${value}px`;

const wrapperDeclarations = (v, z) => {
  const out = [];
  if (v.mode === "free") {
    out.push("position:absolute");
    out.push(v.w ? `left:min(${px(v.x)},max(0px,calc(100% - ${px(v.w)})))` : `left:${px(v.x)}`);
    out.push(`top:${px(v.y)}`);
    out.push("margin:0");
  } else if (v.x || v.y || z) {
    out.push("position:relative", `left:${px(v.x)}`, `top:${px(v.y)}`);
  }
  if (z) out.push(`z-index:${z}`);
  if (v.w) out.push(`width:min(${px(v.w)},100%)`, "max-width:100%", "box-sizing:border-box");
  if (v.h) out.push(`height:${px(v.h)}`, "box-sizing:border-box");
  if (sidesSet(v.margin)) out.push(`margin:${LAYOUT_SIDES.map((s) => px(v.margin[s])).join(" ")}`);
  if (sidesSet(v.padding)) out.push(`padding:${LAYOUT_SIDES.map((s) => px(v.padding[s])).join(" ")}`);
  return out;
};

// A sized block: its content fills the size chosen in the editor.
const fillRules = (sel) => [
  `${sel}>.hwdi-block{width:100%;height:100%;max-width:none;box-sizing:border-box}`,
  `${sel}>.hwdi-image{display:flex;flex-direction:column}`,
  `${sel}>.hwdi-image img,${sel}>.hwdi-image a{flex:1 1 auto;min-height:0;width:100%!important;height:100%!important;object-fit:contain}`,
  `${sel}>.hwdi-video{display:flex;flex-direction:column}`,
  `${sel}>.hwdi-video>.hwdi-video-frame{flex:1 1 auto;min-height:0;padding-bottom:0!important;height:100%}`,
];

const sameValues = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Devices showing the same values share one rule set; the device prefixes
// are mutually exclusive, so no rule ever has to undo another one.
const deviceGroups = (layout) => {
  const groups = [];
  LAYOUT_DEVICES.forEach((device) => {
    const values = getDeviceLayout(layout, device);
    const group = groups.find((item) => sameValues(item.values, values));
    if (group) group.devices.push(device);
    else groups.push({ values, devices: [device] });
  });
  return groups;
};

const prefixed = (devices, selector) =>
  devices.length === LAYOUT_DEVICES.length ? [selector] : devices.map((device) => `${DEVICE_PREFIX[device]} ${selector}`);

const containerHeights = (children) => {
  // Lowest free child per device: its container grows to hold it.
  const result = {};
  LAYOUT_DEVICES.forEach((device) => {
    let bottom = 0;
    children.forEach((child) => {
      if (!child.settings?.layout || child.visible === false) return;
      const v = getDeviceLayout(child.settings.layout, device);
      if (v.mode === "free") bottom = Math.max(bottom, v.y + (v.h || 40) + v.margin.top + v.margin.bottom);
    });
    result[device] = bottom;
  });
  return result;
};

const containerRules = (selector, children) => {
  const heights = containerHeights(children);
  if (!LAYOUT_DEVICES.some((device) => heights[device] > 0)) return [];
  const rules = [`${selector}{position:relative}`];
  LAYOUT_DEVICES.forEach((device) => {
    if (heights[device] > 0) rules.push(`${DEVICE_PREFIX[device]} ${selector}{min-height:${px(heights[device])}}`);
  });
  return rules;
};

/** The layout stylesheet of a block page ("" when no block has a layout). */
export const buildLayoutCss = (blocks = []) => {
  const rules = [];
  const visit = (block) => {
    if (!block || !SAFE_ID.test(String(block.blockId || ""))) return;
    const layout = block.settings?.layout;
    const sel = `[data-elakay-block-id="${block.blockId}"]`;
    const children = Array.isArray(block.children) ? block.children : [];
    // Container rules first: the block's own rules (a free block is
    // position:absolute) come after and win.
    if (children.length) rules.push(...containerRules(sel, children));
    if (layout && !isDefaultLayout(layout)) {
      const z = normalizeBlockLayout(layout).z;
      deviceGroups(layout).forEach(({ values, devices }) => {
        const declarations = wrapperDeclarations(values, z);
        if (declarations.length) rules.push(`${prefixed(devices, sel).join(",")}{${declarations.join(";")}}`);
        if (values.w || values.h) {
          fillRules(sel).forEach((rule) => {
            const [selectors, body] = [rule.slice(0, rule.indexOf("{")), rule.slice(rule.indexOf("{"))];
            const scoped = selectors.split(",").flatMap((one) => (devices.length === LAYOUT_DEVICES.length ? [one] : devices.map((device) => `${DEVICE_PREFIX[device]} ${one}`)));
            rules.push(`${scoped.join(",")}${body}`);
          });
        }
      });
    }
    children.forEach(visit);
  };
  (Array.isArray(blocks) ? blocks : []).forEach(visit);
  rules.push(...containerRules(".hwdi-block-list", Array.isArray(blocks) ? blocks : []));
  if (!rules.length) return "";
  // The block list is the stacking context of the page's layers.
  return [".hwdi-block-list{position:relative;isolation:isolate}", ...rules].join("\n");
};

/** Which device layout applies for a viewport width (public page). */
export const deviceForWidth = (width) => {
  const value = Number(width) || 0;
  if (value && value <= 600) return "mobile";
  if (value && value <= 1023) return "tablet";
  return "desktop";
};
